import { EmployeeHolidayRecord, EmployeePTORecord } from "../cached-data/types"
import { getFlagEnabled } from "../getFlagValue"

/**
 * Determine whether any PTO or holiday record exists for the given employees.
 *
 * PTO/holiday fulfillment is derived from the timecard summary, so the (expensive)
 * summary only needs to be fetched when the current view actually contains PTO or
 * holiday records for the employees in scope.
 *
 * @param ptoObjects - EmployeePTO records keyed by ID (e.g. from `selectPTOObjects`).
 * @param holidayObjects - EmployeeHoliday records keyed by ID (e.g. from `selectHolidayObjects`).
 * @param employeeIds - The employees to check; typically the employees in the current view.
 * @returns True if at least one record belongs to one of `employeeIds`, otherwise false.
 */
export const hasPtoOrHolidaysForEmployees = (ptoObjects: Record<number, EmployeePTORecord>, holidayObjects: Record<string, EmployeeHolidayRecord>, employeeIds: Iterable<number>): boolean => {
    const idSet = new Set(employeeIds)
    if (idSet.size === 0) return false
    return Object.values(ptoObjects).some(record => idSet.has(record.employee)) || Object.values(holidayObjects).some(record => idSet.has(record.employee))
}

/**
 * Decide whether the employee `tk_summary` endpoint needs to be (re)fetched.
 *
 * The summary powers three consumers: work-rule evaluation, CICO diffs, and PTO/holiday
 * fulfillment. The endpoint is expensive, so this gate keeps us from calling it when
 * nothing in the current view depends on it.
 *
 * Behavior:
 *   - When the `WA-9523-tk-summary-austerity` flag is OFF, preserve the legacy behavior of
 *     fetching whenever work rules are enabled (`WA-8985-work-rules`).
 *   - When the flag is ON, fetch only when a consumer is active: CICO is enabled, the
 *     work-rule exception filter is on, or the view has PTO/holiday records. PTO drives the
 *     fetch independently of work rules so PTO fulfillment stays correct on its own.
 *
 * @param params.cicoEnabled - Whether the CICO feature is enabled for the current user.
 * @param params.workRulesExceptionEnabled - Whether the work-rule exception filter is active.
 * @param params.hasPtoOrHolidays - Whether the view has PTO/holiday records for its employees.
 * @returns True if the summary should be fetched, otherwise false.
 */
export const shouldFetchTkSummary = (params: { cicoEnabled: boolean; workRulesExceptionEnabled: boolean; hasPtoOrHolidays: boolean }): boolean => {
    if (!getFlagEnabled("WA-9523-tk-summary-austerity")) {
        return getFlagEnabled("WA-8985-work-rules")
    }
    return params.cicoEnabled || params.workRulesExceptionEnabled || params.hasPtoOrHolidays
}
